import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import axios from "axios";

import { printRow } from "./ImtexBadgeDimension";
import { API_ENDPOINT, ONSITE_MODULE_ITEMS } from "../../utils/constants";
import CustomCircularP from "../../widgets/CustomCircularP";
import { SPOT_REG_CATEGORIES } from "./SpotRegistration";
import { getOnsiteHeader } from "./utils";

const SearchPrint = () => {
  const [selectedFilter, setSelectedFilter] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [debouncedSearchQ, setDebouncedSearchQ] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // Added error state
  const [data, setData] = useState();
  const [page, setPage] = useState(1); // Note: Add pagination UI if you plan to change this

  const keyboardRef = useRef(null);

  // 1. Clean Native Debounce (Replaces lodash)
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchQ(searchQ);
    }, 300);

    return () => clearTimeout(timerId); // Cleanup on unmount or re-render
  }, [searchQ]);

  const handleSearch = (e) => {
    const vl = e.target.value;
    setSearchQ(vl);

    // 2. Sync physical keyboard typing with the virtual keyboard state
    if (keyboardRef.current) {
      keyboardRef.current.setInput(vl);
    }
  };

  const fetchVisitors = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.get(
        `${API_ENDPOINT}/user/get-onsite-users?page=${page}&sq=${debouncedSearchQ}&usertype=${selectedFilter}`,
        getOnsiteHeader(ONSITE_MODULE_ITEMS.SEARCH_AND_PRINT)
      );
      setData(data);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to fetch users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearchQ, selectedFilter]);

  const onKeyPress = (button) => {
    console.log("Button pressed", button);
  };

  return (
    <Box>
      <Box mt={2} display="flex">
        <Box flex={1} ml={2} mr={2}>
          <Paper
            sx={{
              padding: "2px 4px 2px 10px",
              backgroundColor: "#f4f4f4",
              display: "flex",
              alignItems: "center",
            }}
          >
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search Users by Name/Company Name/Email/Mobile"
              value={searchQ}
              onChange={handleSearch}
              inputProps={{
                "aria-label": "Search Users",
              }}
            />
            {/* Removed type="submit" as it's not inside a form */}
            <IconButton
              sx={{ p: "8px" }}
              aria-label="search"
              onClick={fetchVisitors}
            >
              <SearchIcon />
            </IconButton>
          </Paper>
          <Keyboard
            keyboardRef={(r) => (keyboardRef.current = r)} // Attach ref here
            onChange={(input) => setSearchQ(input)}
            onKeyPress={onKeyPress}
          />
        </Box>
        <FormControl
          sx={{ maxWidth: "200px", alignSelf: "center" }}
          fullWidth
          size="small"
        >
          <InputLabel id="filter-select-label">Filter by</InputLabel>
          <Select
            labelId="filter-select-label"
            id="filter-select"
            name="selectedCountry"
            value={selectedFilter}
            label="Filter by"
            onChange={(e) => setSelectedFilter(e.target.value)}
          >
            <MenuItem value="">ALL</MenuItem>
            {SPOT_REG_CATEGORIES.map((target) => (
              <MenuItem key={target} value={target}>
                {target}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ position: "relative", minHeight: "200px" }}>
        <CustomCircularP show={loading} />

        {error && (
          <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
            {error}
          </Typography>
        )}

        <TableContainer sx={{ mt: 2 }} component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="users table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Designation</TableCell>
                <TableCell align="right">Company</TableCell>
                <TableCell align="right">Email</TableCell>
                <TableCell align="right">Phone</TableCell>
                <TableCell align="right">Print</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* 3. Empty State Handling */}
              {!loading && (!data?.data || data.data.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                data?.data?.map((row) => (
                  <TableRow
                    key={row.user_id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {row.title} {row.firstName} {row.lastName} {row.name}
                    </TableCell>
                    <TableCell align="right">
                      {row.designation} {row.education}
                    </TableCell>
                    <TableCell align="right">{row.companyName}</TableCell>
                    <TableCell align="right">{row.email}</TableCell>
                    <TableCell align="right">{row.mobileNo}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => printRow(row)}>
                        <LocalPrintshopIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SearchPrint;
